/*
 * Reveals the email-signup confirmation after a successful native submit.
 *
 * Shopify's {% form "customer" %} (newsletter) submits to /contact and then
 * REDIRECTS back with `?customer_posted=true`. Unlike the in-place `contact`
 * form, `form.posted_successfully?` does not survive that redirect, and Liquid
 * cannot read the query string — so the server-rendered success branch never
 * fires. This script reads the reliable `customer_posted=true` flag client-side
 * and reveals the (otherwise hidden) confirmation message, then scrolls it into
 * view. It is a no-op on every other page load.
 *
 * NOTE: a no-reload version (submit into a hidden iframe) was tried and removed.
 * Shopify's spam-protection challenge page (/challenge) is served with
 * `X-Frame-Options: DENY` + `frame-ancestors 'none'`, so it cannot render inside
 * an iframe — the challenge silently dead-ends and the submit never completes.
 * The captcha must run in a top-level navigation, so a reload is unavoidable.
 */
(function emailSignupConfirmation() {
  /**
   * Un-hide the confirmation inside a block.
   * @param {Element} block
   * @returns {Element | null} the revealed confirmation, if any
   */
  const revealConfirmation = block => {
    const confirmation = /** @type {HTMLElement | null} */ (block.querySelector("[data-email-signup-confirmation]"));
    if (!confirmation) {
      return null;
    }
    confirmation.hidden = false;

    // Announce to assistive tech. Populating an empty live region is a reliable
    // announce trigger; un-hiding a pre-filled role="status" is not.
    const announce = block.querySelector("[data-email-signup-announce]");
    const text = confirmation.querySelector(".email-signup__message-text");
    if (announce && text) {
      announce.textContent = text.textContent.trim();
    }
    return confirmation;
  };

  const reveal = () => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("customer_posted") !== "true") {
      return;
    }

    // Scope to the block that was actually submitted: Shopify's redirect appends a
    // #EmailSignup-<id> anchor matching that block's form id, so reveal/announce/scroll
    // only that one. Fall back to all blocks if the anchor is missing. (Read the hash
    // before the replaceState below strips it.)
    const submittedId = globalThis.location.hash.slice(1);
    const submittedForm = submittedId ? document.querySelector(`#${CSS.escape(submittedId)}`) : null;
    const submittedBlock = submittedForm?.closest(".email-signup-block");
    const blocks = submittedBlock ? [submittedBlock] : document.querySelectorAll(".email-signup-block");

    let firstMessage = null;
    for (const block of blocks) {
      const message = revealConfirmation(block);
      firstMessage = firstMessage ?? message;
    }

    if (firstMessage) {
      // The post-submit redirect's #EmailSignup-… anchor usually lands the page on
      // the footer already, so only correct the position if the confirmation isn't
      // visible. Deferred so it runs after the theme's own load-time scroll/restoration.
      globalThis.setTimeout(() => {
        const rect = firstMessage.getBoundingClientRect();
        const viewport = globalThis.innerHeight || document.documentElement.clientHeight;
        if (rect.top < 0 || rect.bottom > viewport) {
          firstMessage.scrollIntoView({ block: "center" });
        }
      }, 100);
    }

    // Drop the flag (and Shopify's #EmailSignup-… form anchor) so the post-submit
    // URL is clean and a manual refresh doesn't re-show the confirmation.
    params.delete("customer_posted");
    const query = params.toString();
    const { pathname } = globalThis.location;
    globalThis.history.replaceState({}, "", `${pathname}${query ? `?${query}` : ""}`);
  };

  // Show a spinner on the button while the native submit + captcha + redirect run,
  // so the button doesn't look dead for several seconds. Purely visual: the submit
  // is NOT intercepted, so Shopify's spam-protection flow is untouched.
  const showLoadingOnSubmit = () => {
    /** @type {Element[]} */
    const buttons = [];
    // Single shared stall-timer id: only one submit is ever in flight (a submit
    // navigates the page away), and tracking it lets each new submit cancel the
    // previous one's pending clear so an earlier timer can't strip a later
    // submit's spinner. clearLoading also runs on bfcache restore (pageshow).
    /** @type {number | undefined} */
    let stallTimer;
    const clearLoading = () => {
      globalThis.clearTimeout(stallTimer);
      for (const button of buttons) {
        button.classList.remove("email-signup__button--loading");
      }
    };

    // Hoisted out of the loop (rather than an inline closure per form) so the shared
    // stallTimer reference doesn't trip no-loop-func; the submitted form is event.currentTarget.
    /** @param {Event} event */
    const handleSubmit = event => {
      const form = /** @type {HTMLFormElement} */ (event.currentTarget);
      form.querySelector(".email-signup__button")?.classList.add("email-signup__button--loading");
      // Safety net: a successful submit navigates away (and the spinner dies with the
      // page, or is cleared by the pageshow handler on bfcache restore). But the
      // spam-protection challenge can silently dead-end without navigating — see the
      // header note — which would otherwise leave the button spinning AND unclickable
      // (pointer-events:none) until a manual reload. Real signups finish in a few
      // seconds; clear well past that so this only ever fires on a genuine stall.
      // Cancel any prior pending timer first so a re-submit (e.g. Enter in the input,
      // which pointer-events:none can't block) doesn't inherit an older 30s deadline.
      globalThis.clearTimeout(stallTimer);
      stallTimer = globalThis.setTimeout(clearLoading, 30_000);
    };

    for (const form of document.querySelectorAll('.email-signup-block form[action*="/contact"]')) {
      const button = form.querySelector(".email-signup__button");
      if (!button) {
        continue;
      }
      buttons.push(button);
      form.addEventListener("submit", handleSubmit);
    }

    if (buttons.length === 0) {
      return;
    }

    // One listener clears the spinner if the shopper returns via the back button
    // (bfcache restore), rather than one per form.
    globalThis.addEventListener("pageshow", clearLoading);
  };

  const init = () => {
    reveal();
    showLoadingOnSubmit();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
